import React from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, CalendarDays } from "lucide-react";

// Editor for extraordinary / holiday hours and temporary closures (e.g. Natale, ferie).
// Each entry: { id, date, date_to?, label, closed, from, to }
export default function SpecialHoursEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];

  const update = (id, patch) => onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const remove = (id) => onChange(items.filter((it) => it.id !== id));
  const add = () => {
    const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : String(Date.now());
    onChange([...items, { id, date: "", date_to: "", label: "", closed: true, from: "09:00", to: "19:00" }]);
  };

  return (
    <div className="space-y-3" data-testid="special-hours-editor">
      {items.length === 0 && (
        <p className="text-sm text-slate-400">Nessun orario speciale. Aggiungi festività, ferie o chiusure straordinarie.</p>
      )}

      {items.map((it) => (
        <div key={it.id} className="rounded-xl border border-slate-200 p-3 space-y-2.5" data-testid={`special-item-${it.id}`}>
          <div className="flex items-center gap-2">
            <Input value={it.label} onChange={(e) => update(it.id, { label: e.target.value })}
              placeholder="Es. Natale, Ferie, Ponte" className="h-10 rounded-lg flex-1" data-testid={`special-label-${it.id}`} />
            <button type="button" onClick={() => remove(it.id)} data-testid={`special-remove-${it.id}`}
              className="h-10 w-10 rounded-lg border border-slate-200 text-red-500 flex items-center justify-center shrink-0 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <CalendarDays className="h-4 w-4 text-slate-400 shrink-0" />
            <input type="date" value={it.date || ""} onChange={(e) => update(it.id, { date: e.target.value })}
              data-testid={`special-date-${it.id}`} className="h-10 px-2 rounded-lg border border-slate-200 text-sm flex-1 min-w-0" />
            <span className="text-slate-400">→</span>
            <input type="date" value={it.date_to || ""} onChange={(e) => update(it.id, { date_to: e.target.value })}
              data-testid={`special-dateto-${it.id}`} className="h-10 px-2 rounded-lg border border-slate-200 text-sm flex-1 min-w-0" placeholder="fine (facolt.)" />
          </div>
          <p className="text-[11px] text-slate-400 -mt-1">Lascia vuota la seconda data per un solo giorno.</p>

          <div className="flex items-center justify-between">
            {it.closed ? (
              <span className="text-sm text-slate-500">Chiuso tutto il giorno</span>
            ) : (
              <div className="flex items-center gap-2">
                <input type="time" value={it.from} onChange={(e) => update(it.id, { from: e.target.value })}
                  data-testid={`special-from-${it.id}`} className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
                <span className="text-slate-400">–</span>
                <input type="time" value={it.to} onChange={(e) => update(it.id, { to: e.target.value })}
                  data-testid={`special-to-${it.id}`} className="h-9 px-2 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-xs text-slate-400">{it.closed ? "Chiuso" : "Aperto"}</span>
              <Switch checked={!it.closed} onCheckedChange={(v) => update(it.id, { closed: !v })} data-testid={`special-open-${it.id}`} />
            </div>
          </div>
        </div>
      ))}

      <button type="button" onClick={add} data-testid="special-add"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E63946] hover:underline">
        <Plus className="h-4 w-4" /> Aggiungi giorno speciale
      </button>
    </div>
  );
}
